import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

const VideoShowcase = () => {
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);
  const audioTriggeredRef = useRef(false);

  // Check if the device is mobile/tablet
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Effect to handle music toggling when video plays
  useEffect(() => {
    // Check user's saved music preference first
    const STORAGE_KEY = 'atlasmoth-music-preference';
    const savedPreference = localStorage.getItem(STORAGE_KEY);
    const shouldPlayMusic = savedPreference !== null ? savedPreference === 'true' : true;
    
    // Only attempt to play if the user preference allows it
    if (!shouldPlayMusic) {
      console.log('Respecting user preference: music off');
      return;
    }
    
    const triggerAudio = () => {
      if (audioTriggeredRef.current) return;
      
      // Find the audio element from MusicToggle component
      const audioElement = document.querySelector('audio[src="/dummy-music.mp3"]');
      
      if (audioElement && !audioElement.paused) {
        // Audio is already playing, don't try to play it again
        audioTriggeredRef.current = true;
        return;
      }
      
      if (audioElement) {
        audioElement.play()
          .then(() => {
            audioTriggeredRef.current = true;
            console.log('Music started successfully');
          })
          .catch(err => {
            console.log('Could not autoplay music:', err);
          });
      }
    };

    // Try triggering audio when component mounts
    triggerAudio();

    // Setup video event listeners
    const video = videoRef.current;
    if (video) {
      video.addEventListener('play', triggerAudio);
      video.addEventListener('canplaythrough', triggerAudio);
    }

    // User interaction can also trigger audio
    const handleInteraction = () => {
      if (shouldPlayMusic) {
        triggerAudio();
      }
      // Remove these listeners after first interaction
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };

    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);

    // Listen for changes to the music preference
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        const newPreference = e.newValue === 'true';
        const audioElement = document.querySelector('audio[src="/dummy-music.mp3"]');
        
        if (!newPreference && audioElement && !audioElement.paused) {
          audioElement.pause();
        } else if (newPreference && audioElement && audioElement.paused) {
          audioElement.play().catch(err => console.log('Could not play music:', err));
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (video) {
        video.removeEventListener('play', triggerAudio);
        video.removeEventListener('canplaythrough', triggerAudio);
      }
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <motion.section 
      className="w-full relative overflow-hidden bg-black"
      style={{
        // Adjusted height for better mobile/tablet experience
        height: isMobile ? '60vh' : '100vh',
        maxHeight: isMobile ? '600px' : 'none',
      }}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        <video 
          ref={videoRef}
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover md:object-center object-[center_30%]"
          style={{
            // Ensures video stays in proportion while filling space
            minWidth: '100%',
            minHeight: '100%',
            // Optional: Use object-position as inline style for more specific control
            // objectPosition: isMobile ? 'center 30%' : 'center center',
          }}
          // Optimize for mobile performance
          preload="auto"
        >
          <source src="/showcase-video.mp4" type="video/mp4" />
          {/* Fallback for older browsers */}
          Your browser does not support the video tag.
        </video>
      </div>
    </motion.section>
  );
};

export default VideoShowcase;
